/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import MiniHLineChart from "./MiniHLineChart";
import VerticalLegend from "../../LegendMenu/Legend";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { calculateYAxisDomain, formatDataLabel, getHeaderTextStyle, hasLowNSize, measureTextWidth } from "../../BarCharts/chartHelpers";
import { ChartContainer } from "../../components/ChartContainer";
import { ChartFootnote } from "../../components/ChartFootnote";
import { NSizeLabel } from "../../components/NSizeLabel";
import { YAxisScale } from "../../components/YAxisScale";
import { EllipsisText } from "../../components/EllipsisText";
import { chartConfig } from "../../chartConfig";
import HorizontalLegend from "../../LegendMenu/HorizontalLegend";
import ChartTabularView from "../../components/ChartTabularView";
import ChartViewToggle from "../../components/ChartViewToggle";
import { ChartRenderedFromType, IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import { useScaledConfig } from "../../ChartScaleContext";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { AxisLabel } from "../../components/AxisLabel";
import ChartHeader from "../../components/ChartHeader";
import ChartError from "../../ChartError";

/* -------------------- Types -------------------- */

type WaveDataPoint = {
	wave: string;
	value: number;
	n_size?: number | string;
	stat_marker?: IStatMarker | null;
};

type HierarchyGroup = {
	path: string[];
	groupKey: string;
	attributes: string[];
	attributeNSize: Record<string, { min: number | null; max: number | null }>;
	productData: Record<string, Record<string, WaveDataPoint[]>>;
};

type HeaderCell = {
	label: string;
	colSpan: number;
	levelIndex: number;
};

type CellType = "header" | "nsize" | "chart" | "attribute";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	totalRows: number;
	totalCols: number;
	isLastInGroup: boolean;
	isFirstInGroup: boolean;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

/* -------------------- Border Helper Functions -------------------- */
const createBorderConfig = (colorsConfig: any) => {
	const solidBorder = `1px solid ${colorsConfig.borders.solid}`;
	const dashedBorder = `1px dashed ${colorsConfig.borders.solid}`;
	const lightBorder = `1px solid ${colorsConfig.borders.lightGray}`;
	const groupBorder = `1px solid ${colorsConfig.borders.gray}`;

	return {
		solid: solidBorder,
		dashed: dashedBorder,
		light: lightBorder,
		group: groupBorder,
	};
};

const getCellBorders = (
	cellType: CellType,
	position: ICellPosition,
	borderConfig: { solid: string; dashed: string; light: string; group: string },
	hierarchyDepth: number = 0,
): IBorderConfig => {
	const { rowIndex, colIndex, totalCols, isLastInGroup } = position;
	const isFirstRow = rowIndex === 0;
	const isLastCol = colIndex === totalCols - 1;

	// Only show dashed borders if hierarchy depth >= 3
	const showDashedBorders = hierarchyDepth >= 3;

	switch (cellType) {
		case "header":
			return {
				borderTop: borderConfig.solid,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "nsize":
			return {
				borderBottom: borderConfig.solid,
				// borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : showDashedBorders && !isLastInGroup ? borderConfig.dashed : undefined,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
				borderTop: isFirstRow ? borderConfig.solid : undefined,
			};

		case "chart":
			return {
				borderBottom: borderConfig.solid,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "attribute":
			return {
				borderTop: borderConfig.solid,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		default:
			return {};
	}
};

/* -------------------- Optimized Lazy Border Cache -------------------- */
const useBorderGetter = (colorsConfig: any, totalRows: number, totalCols: number, groupBoundaries: Set<number>, hierarchyDepth: number = 0) => {
	const cacheRef = useRef<Map<string, IBorderConfig>>(new Map());
	const borderConfigRef = useRef(createBorderConfig(colorsConfig));

	useEffect(() => {
		borderConfigRef.current = createBorderConfig(colorsConfig);
		cacheRef.current.clear();
	}, [colorsConfig]);

	const getBorders = useCallback(
		(cellType: CellType, rowIndex: number, colIndex: number): IBorderConfig => {
			const isLastInGroup = groupBoundaries.has(colIndex);
			const isFirstInGroup = colIndex === 0 || groupBoundaries.has(colIndex - 1);
			const key = `${cellType}:${rowIndex}:${colIndex}:${isLastInGroup}:${isFirstInGroup}:${hierarchyDepth}`;

			const cached = cacheRef.current.get(key);
			if (cached !== undefined) {
				return cached;
			}

			const position: ICellPosition = {
				rowIndex,
				colIndex,
				totalRows,
				totalCols,
				isLastInGroup,
				isFirstInGroup,
			};

			const borders = getCellBorders(cellType, position, borderConfigRef.current, hierarchyDepth);
			cacheRef.current.set(key, borders);

			return borders;
		},
		[totalRows, totalCols, groupBoundaries, hierarchyDepth],
	);

	return getBorders;
};

/* -------------------- Helper Functions -------------------- */

/**
 * Reorder discovered keys at a 0-based nesting depth using the authoritative
 * values from filter_hierarchy. Hierarchies are sorted by `order` ascending
 * and indexed by ordinal position. Stragglers append in discovery order.
 */
const resolveKeysByHierarchyOrder = (
	discoveredKeys: string[],
	filterHierarchy: any[] | undefined,
	depth: number,
): string[] => {
	if (!filterHierarchy || filterHierarchy.length === 0) return discoveredKeys;

	const sorted = [...filterHierarchy].sort((a, b) => a.order - b.order);
	const hAt = sorted[depth];
	if (!hAt || !Array.isArray(hAt.values) || hAt.values.length === 0) return discoveredKeys;

	const remaining = new Set(discoveredKeys);
	const ordered: string[] = [];
	hAt.values.forEach((v: string) => {
		if (remaining.has(v)) {
			ordered.push(v);
			remaining.delete(v);
		}
	});
	discoveredKeys.forEach((k) => {
		if (remaining.has(k)) ordered.push(k);
	});
	return ordered;
};

/**
 * Reorder a discovered list against an authoritative values array.
 * Stragglers append in discovery order so nothing is dropped.
 */
const reorderByValues = (discovered: string[], values: string[] | undefined): string[] => {
	if (!values || values.length === 0) return discovered;
	const remaining = new Set(discovered);
	const ordered: string[] = [];
	values.forEach((v) => {
		if (remaining.has(v)) {
			ordered.push(v);
			remaining.delete(v);
		}
	});
	discovered.forEach((d) => {
		if (remaining.has(d)) ordered.push(d);
	});
	return ordered;
};

/**
 * Processes a wave array (leaf data) into HierarchyGroup format.
 * `waveOrderValues` orders the x-axis wave labels (deepest filter_hierarchy values).
 * `seriesOrder` orders products (from chart_config.ser_color).
 */
const processWaveArray = (
	waveArray: any[],
	currentPath: string[],
	groupKey: string,
	waveOrderValues?: string[],
	seriesOrder?: string[],
): HierarchyGroup | null => {
	if (!Array.isArray(waveArray) || waveArray.length === 0) return null;

	const firstItem = waveArray[0];
	if (!firstItem?.xAxis || !firstItem?.series) return null;

	const wavesSet = new Set<string>();
	const productsSet = new Set<string>();

	waveArray.forEach((waveItem: any) => {
		if (waveItem.xAxis) wavesSet.add(waveItem.xAxis);
		if (waveItem.series) {
			waveItem.series.forEach((s: any) => {
				if (s.name) productsSet.add(s.name);
			});
		}
	});

	// Authoritative reorderings — fall back to discovery order if no values supplied.
	const waves = reorderByValues(Array.from(wavesSet), waveOrderValues);
	const products = reorderByValues(Array.from(productsSet), seriesOrder);

	const productData: Record<string, Record<string, WaveDataPoint[]>> = {};
	const attributeNSize: Record<string, { min: number | null; max: number | null }> = {};

	products.forEach((product) => {
		productData[product] = {};
		waves.forEach((wave) => {
			productData[product][wave] = [];
		});
	});

	waveArray.forEach((waveItem: any) => {
		const waveName = waveItem.xAxis;

		if (!attributeNSize[waveName]) {
			// Compute n-size range from non-zero / non-"No data" series entries
			// so a single suppressed series doesn't drag the displayed min to 0.
			const nonZeroNs: number[] = [];
			if (Array.isArray(waveItem.series)) {
				waveItem.series.forEach((s: any) => {
					if (s?.n_size === null || s?.n_size === undefined) return;
					if (typeof s.n_size === "string" && s.n_size.toLowerCase() === "no data") return;
					const n = Number(s.n_size);
					if (!isNaN(n) && n > 0) nonZeroNs.push(n);
				});
			}

			if (nonZeroNs.length > 0) {
				attributeNSize[waveName] = {
					min: Math.min(...nonZeroNs),
					max: Math.max(...nonZeroNs),
				};
			} else if (waveItem.n_size) {
				const rMin = Number(waveItem.n_size.min);
				const rMax = Number(waveItem.n_size.max);
				attributeNSize[waveName] = {
					min: !isNaN(rMin) && rMin > 0 ? rMin : !isNaN(rMax) && rMax > 0 ? rMax : null,
					max: !isNaN(rMax) && rMax > 0 ? rMax : !isNaN(rMin) && rMin > 0 ? rMin : null,
				};
			}
		}

		if (waveItem.series && Array.isArray(waveItem.series)) {
			waveItem.series.forEach((seriesItem: any) => {
				const productName = seriesItem.name;
				const value = seriesItem.value === "No data" ? null : seriesItem.value;

				if (value !== null && typeof value === "number" && productData[productName]) {
					productData[productName][waveName].push({
						wave: waveName,
						value: value,
						n_size: seriesItem.n_size === "No data" ? null : seriesItem.n_size,
						stat_marker: seriesItem.stat_marker,
					});
				}
			});
		}
	});

	return {
		path: currentPath,
		groupKey: groupKey,
		attributes: waves,
		attributeNSize,
		productData,
	};
};

/**
 * Checks if an array is a valid wave array (leaf data)
 */
const isWaveArray = (arr: any): boolean => {
	return Array.isArray(arr) && arr.length > 0 && arr[0]?.xAxis !== undefined && arr[0]?.series !== undefined;
};

/**
 * Builds hierarchy groups from various data formats:
 * - Flat array: [{xAxis, series}, ...]
 * - Single level object: { "Asian": [{xAxis, series}, ...] }
 * - Multi-level nested: { "Asian": { "Male": [{xAxis, series}, ...] } }
 */
const buildHierarchyGroups = (
	dataNode: any,
	filterHierarchy?: any[],
	seriesOrder?: string[],
	currentPath: string[] = [],
): HierarchyGroup[] => {
	const sortedHier = filterHierarchy ? [...filterHierarchy].sort((a, b) => a.order - b.order) : [];

	// Case 1: Flat array at root level
	if (isWaveArray(dataNode)) {
		const leafValues = sortedHier[currentPath.length]?.values;
		const group = processWaveArray(dataNode, currentPath, currentPath[currentPath.length - 1] || "Total", leafValues, seriesOrder);
		return group ? [group] : [];
	}

	// Case 2: Not an object or null/undefined
	if (!dataNode || typeof dataNode !== "object" || Array.isArray(dataNode)) {
		return [];
	}

	const keys = Object.keys(dataNode);
	if (keys.length === 0) return [];

	// Reorder nested keys at this depth via authoritative filter_hierarchy values.
	const orderedKeys = resolveKeysByHierarchyOrder(keys, filterHierarchy, currentPath.length);

	const groups: HierarchyGroup[] = [];
	orderedKeys.forEach((key) => {
		const value = dataNode[key];

		// Case 3: Value is a wave array (leaf level)
		if (isWaveArray(value)) {
			const leafValues = sortedHier[currentPath.length + 1]?.values;
			const group = processWaveArray(value, [...currentPath, key], key, leafValues, seriesOrder);
			if (group) groups.push(group);
		}
		// Case 4: Value is a nested object - recurse
		else if (value && typeof value === "object" && !Array.isArray(value)) {
			const childGroups = buildHierarchyGroups(value, filterHierarchy, seriesOrder, [...currentPath, key]);
			groups.push(...childGroups);
		}
		// Case 5: Skip primitives and non-wave arrays
	});

	return groups;
};

const buildHeaderRow = (groups: HierarchyGroup[], levelIndex: number, visibleWaves: string[]): HeaderCell[] => {
	const headers: HeaderCell[] = [];
	if (groups.length === 0) return headers;

	let currentLabel = groups[0].path[levelIndex];
	let currentSpan = 0;
	let prevGroup: HierarchyGroup | null = null;

	groups.forEach((group, i) => {
		const label = group.path[levelIndex];
		// Calculate span based on visible attributes only
		const visibleAttrs = group.attributes.filter((attr) => visibleWaves.includes(attr));
		const attrCount = visibleAttrs.length;

		// Skip groups with no visible attributes
		if (attrCount === 0) return;

		let parentChanged = false;

		if (levelIndex > 0 && prevGroup) {
			for (let p = 0; p < levelIndex; p++) {
				if (group.path[p] !== prevGroup.path[p]) {
					parentChanged = true;
					break;
				}
			}
		}

		if (prevGroup !== null && (label !== currentLabel || parentChanged)) {
			if (currentSpan > 0) {
				headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
			}
			currentLabel = label;
			currentSpan = attrCount;
		} else {
			currentSpan += attrCount;
		}
		prevGroup = group;
	});

	if (currentSpan > 0) {
		headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
	}

	return headers;
};

const extractAllValues = (groups: HierarchyGroup[]): number[] => {
	const values: number[] = [];
	groups.forEach((group) => {
		Object.values(group.productData).forEach((attrData) => {
			Object.values(attrData).forEach((wavePoints) => {
				wavePoints.forEach((point) => {
					if (typeof point.value === "number" && isFinite(point.value)) {
						values.push(point.value);
					}
				});
			});
		});
	});
	return values;
};

const getAllWaves = (groups: HierarchyGroup[], waveOrderValues?: string[]): string[] => {
	const wavesSet = new Set<string>();
	groups.forEach((group) => {
		group.attributes.forEach((attr) => wavesSet.add(attr));
	});
	return reorderByValues(Array.from(wavesSet), waveOrderValues);
};

const getAllProducts = (groups: HierarchyGroup[], seriesOrder?: string[]): string[] => {
	const productsSet = new Set<string>();
	groups.forEach((group) => {
		Object.keys(group.productData).forEach((p) => productsSet.add(p));
	});
	return reorderByValues(Array.from(productsSet), seriesOrder);
};

/* -------------------- Main Component -------------------- */
const HLineChart: React.FC<{
	chartResponse: any;
	chartRules?: Record<string, any>;
	isChartView: boolean;
	setIsChartView: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}> = ({ chartResponse, chartRules, isChartView, setIsChartView, renderedFrom }) => {
	const colorsConfig = chartConfig.getColorsConfig();
	const lineChartConfig = chartConfig.getHorizontalLineConfig();

	const apiData = chartResponse?.data?.[0]?.chart_data;
	// Determines if chart_data is missing/empty/invalid to render fallback UI
	const isDataEmpty = useMemo(() => {
		if (apiData === null || apiData === undefined) return true;
		if (Array.isArray(apiData) && apiData.length === 0) return true;
		if (typeof apiData !== "object") return true; // guard against primitives
		if (!Array.isArray(apiData) && Object.keys(apiData).length === 0) return true;
		return false;
	}, [apiData]);
	const explicitHierarchy = useMemo(() => chartResponse.filter_hierarchy || [], [chartResponse.filter_hierarchy]);
	const productColors = useMemo(() => chartResponse?.chart_config?.ser_color || {}, [chartResponse?.chart_config?.ser_color]);
	const waveMarkers = chartResponse?.chart_config?.lc_marker_sp || {};
	const labelFormat = chartResponse?.chart_config?.dlabel_fmt || "##%";
	const lineWeight = chartResponse?.chart_config?.line_chart_line_weight || lineChartConfig.lineWeight;
	const smoothLine = chartResponse?.chart_config?.line_smoothening === "Yes";
	const statTestConfig: IStatTestConfig | null = chartResponse?.chart_config?.stat_test_config ?? null;
	const scaled = useScaledConfig();
	// Add after const scaled = useScaledConfig();
	const ASK_KURAL_MAX_CHART_HEIGHT = 150;
	const isRenderedFromAskKural = renderedFrom === ChartRenderedFromType.AskKural;
	const isRenderedFromAskKuralInline = renderedFrom === ChartRenderedFromType.AskKuralInline;
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const isEffectiveMultiTile = isMultiTileSlide || isRenderedFromAskKuralInline;
	const [visibleProducts, setVisibleProducts] = useState<string[]>([]);
	const [visibleWaves, setVisibleWaves] = useState<string[]>([]);
	const [expandedWaves, setExpandedWaves] = useState(false);
	const [expandedProducts, setExpandedProducts] = useState(false);
	const [recalcKey, setRecalcKey] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	const contentAreaRef = useRef<HTMLDivElement>(null);
	const chartAreaRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLDivElement>(null);
	const headerSectionRef = useRef<HTMLDivElement>(null);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);

	const containerWidth = useContainerWidth(containerRef);
	// Reusable stabilized height pattern — add to HClusteredBarChart, HStackedBarChart,
	// VStackedBarChart, HLineChart, VLineChart
	const rawContentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	const stableContentAreaHeightRef = useRef<number>(0);
	const measureCountRef = useRef<number>(0);
	const showAxisColumn = !isEffectiveMultiTile || isRenderedFromAskKural;

	const contentAreaHeight = useMemo(() => {
		const raw = rawContentAreaHeight || 0;
		const prev = stableContentAreaHeightRef.current;

		if (prev === 0) {
			stableContentAreaHeightRef.current = raw;
			measureCountRef.current = 1;
			return raw;
		}

		measureCountRef.current += 1;

		// Lock after 2 measurements for AskKural to prevent oscillation
		if (isRenderedFromAskKuralInline && measureCountRef.current > 2) {
			return prev;
		}

		// After warm-up, require a larger delta to defeat header-wrap-induced
		// oscillation. A real resize easily exceeds this threshold.
		const threshold = measureCountRef.current > 3 ? 24 : 5;
		if (Math.abs(raw - prev) > threshold) {
			stableContentAreaHeightRef.current = raw;
			return raw;
		}

		return prev;
	}, [rawContentAreaHeight, isRenderedFromAskKuralInline]);

	const headerSectionHeight = useContainerHeight(headerSectionRef);
	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef) || contentAreaHeight;

	// Authoritative product order from chart_config.ser_color (consumed by
	// hierarchyGroups, allProducts, and forwarded to ChartTabularView).
	const seriesOrder = useMemo(
		() => (productColors ? Object.keys(productColors) : []),
		[productColors],
	);

	const hierarchyGroups = useMemo(() => {
		if (isDataEmpty) return [];
		const groups = buildHierarchyGroups(apiData, explicitHierarchy, seriesOrder);
		return groups;
	}, [apiData, explicitHierarchy, seriesOrder, isDataEmpty]);

	// Skip hasLowNSize when data is empty — guard against undefined/empty internals
	const showLowNSize = useMemo(
		() => (isDataEmpty ? false : hasLowNSize(apiData, { threshold: 30 })),
		[apiData, isDataEmpty],
	);

	const minColumnWidth = chartRules?.line_chart?.chartColumnWidth?.min ?? lineChartConfig.minColumnWidth;
	const maxColumnWidth = minColumnWidth * 1.75;

	const attrColumnWidth = useMemo(() => {
		if (hierarchyGroups.length === 0) return minColumnWidth;

		const allAttrs = hierarchyGroups.flatMap((g) => g.attributes);
		if (allAttrs.length === 0) return minColumnWidth;

		const maxTextWidth = Math.max(...allAttrs.map((attr) => measureTextWidth(attr, "600 11px sans-serif")));
		const requiredWidth = maxTextWidth + 16;
		const result = Math.min(Math.max(minColumnWidth, requiredWidth), maxColumnWidth);
		return result;
	}, [hierarchyGroups, minColumnWidth, maxColumnWidth]);

	const effectiveHierarchyLevels = useMemo(() => {
		if (explicitHierarchy && explicitHierarchy.length > 0) return explicitHierarchy;
		if (hierarchyGroups.length === 0) return [];
		const maxDepth = Math.max(...hierarchyGroups.map((g) => g.path.length));
		if (maxDepth === 0) return [];
		const levels = new Array(maxDepth).fill(null);
		return levels;
	}, [explicitHierarchy, hierarchyGroups]);

	const tableHeaderHeight = useContainerHeight(tableHeaderRef, recalcKey);

	const hasHierarchy = effectiveHierarchyLevels.length > 0;

	const totalColumns = useMemo(() => {
		const total = hierarchyGroups.reduce((sum, g) => {
			const visibleAttrs = g.attributes.filter((attr) => visibleWaves.includes(attr));
			return sum + visibleAttrs.length;
		}, 0);
		return total;
	}, [hierarchyGroups, visibleWaves]);

	const allWaves = useMemo(() => {
		// Wave order = deepest filter_hierarchy values (the leaf dimension on x-axis).
		const sorted = explicitHierarchy ? [...explicitHierarchy].sort((a: any, b: any) => a.order - b.order) : [];
		const leafValues = sorted[sorted.length - 1]?.values;
		return getAllWaves(hierarchyGroups, leafValues);
	}, [hierarchyGroups, explicitHierarchy]);

	const allProducts = useMemo(() => {
		return getAllProducts(hierarchyGroups, seriesOrder);
	}, [hierarchyGroups, seriesOrder]);

	const isFlatStructure = allProducts.length === 1 && allProducts[0] === "_default";
	const hasProducts = !isFlatStructure && allProducts.length > 0 && Object.keys(productColors).length > 0;

	const waveColors = useMemo(() => {
		const defaultColors = ["#1F3A6D", "#E66C37", "#5B7DB8", "#9C4D97", "#009688", "#FFB300"];
		const colors: Record<string, string> = {};
		allWaves.forEach((wave, idx) => {
			colors[wave] = defaultColors[idx % defaultColors.length];
		});
		return colors;
	}, [allWaves]);

	const hierarchyDepth = useMemo(() => {
		if (hierarchyGroups.length === 0) return 0;
		const depth = Math.max(...hierarchyGroups.map((g) => g.path.length));
		return depth;
	}, [hierarchyGroups]);

	const isAISummaryAvailable = chartResponse.chart_config?.ai_summary_enabled ?? false;

	// Calculate group boundaries for border logic
	const groupBoundaries = useMemo(() => {
		const boundaries = new Set<number>();
		let colIndex = 0;
		hierarchyGroups.forEach((group) => {
			const visibleAttrs = group.attributes.filter((attr) => visibleWaves.includes(attr));
			colIndex += visibleAttrs.length;
			if (visibleAttrs.length > 0) {
				boundaries.add(colIndex - 1);
			}
		});
		return boundaries;
	}, [hierarchyGroups, visibleWaves]);

	const getBorders = useBorderGetter(colorsConfig, 1, totalColumns, groupBoundaries, hierarchyDepth);

	// NEW — reset height stabilization refs on view switch so fresh measurements are taken
	useEffect(() => {
		if (isChartView) {
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [isChartView]);

	// One-shot recalc once the table header first reports a real height.
	// On initial paint, tableHeaderHeight is 0 (refs not yet measured), so
	// the chart geometry is computed against a zero header and the rows
	// render at the wrong scale. Reset the contentArea stabilization and
	// bump recalcKey so every ref re-measures against the settled layout.
	const didRecalcAfterHeaderRef = useRef(false);
	useEffect(() => {
		if (didRecalcAfterHeaderRef.current) return;
		if (tableHeaderHeight > 0) {
			didRecalcAfterHeaderRef.current = true;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [tableHeaderHeight]);

	useEffect(() => {
		if (hasProducts) {
			setVisibleProducts(Object.keys(productColors));
		} else {
			setVisibleProducts(allProducts);
		}
	}, [productColors, allProducts, hasProducts]);

	useEffect(() => {
		setVisibleWaves(allWaves);
	}, [allWaves]);

	const yAxisDomain = useMemo(() => {
		const allValues = extractAllValues(hierarchyGroups);
		const domain = calculateYAxisDomain(allValues, labelFormat);
		return domain;
	}, [hierarchyGroups, labelFormat]);

	const columnWidth = useMemo(() => {
		const maxColWidth = chartRules?.line_chart?.chartColumnWidth?.max ?? 200;
		const effectiveAttrWidth = Math.min(attrColumnWidth, maxColWidth);

		if (containerWidth === 0) return effectiveAttrWidth;

		// Only subtract axis column width when it's actually rendered in the grid
		const axisOffset = showAxisColumn ? scaled.geometry.axisColumnWidth : 0;
		const availableWidth = containerWidth - axisOffset;
		const calculatedWidth = availableWidth / totalColumns;
		return Math.max(calculatedWidth, effectiveAttrWidth);
	}, [containerWidth, totalColumns, attrColumnWidth, showAxisColumn, scaled.geometry.axisColumnWidth, chartRules?.line_chart?.chartColumnWidth?.max]);

	const isCompactMode = useMemo(() => {
		// Use small tolerance for floating-point comparison
		const compact = columnWidth <= attrColumnWidth + 0.5;
		return compact;
	}, [columnWidth, attrColumnWidth]);

	// Check if horizontal scrolling is needed
	const needsScroll = useMemo(() => {
		if (containerWidth === 0 || totalColumns === 0) return false;
		const totalContentWidth = totalColumns * columnWidth;
		// Only subtract axis column width when it's actually rendered
		const axisOffset = showAxisColumn ? scaled.geometry.axisColumnWidth : 0;
		const availableWidth = containerWidth - axisOffset;
		return totalContentWidth > availableWidth;
	}, [containerWidth, totalColumns, columnWidth, showAxisColumn, scaled.geometry.axisColumnWidth]);

	// Check if vertical scrolling is needed
	const needsYScroll = useMemo(() => {
		if (contentAreaHeight === 0) return false;
		const resolvedTableHeaderHeight = tableHeaderHeight || 0;
		const totalRequiredHeight = resolvedTableHeaderHeight + lineChartConfig.chartHeight.default;
		const needs = totalRequiredHeight > contentAreaHeight;
		return needs;
	}, [contentAreaHeight, tableHeaderHeight, lineChartConfig.chartHeight.default]);

	const dynamicChartHeight = useMemo(() => {
		const { chartHeight } = lineChartConfig;

		if (contentAreaHeight === 0) {
			return isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : chartHeight.default;
		}

		const resolvedTableHeaderHeight = tableHeaderHeight || 0;
		const scrollbarCompensation = needsScroll ? 6 : 0;
		const availableHeight = contentAreaHeight - resolvedTableHeaderHeight - scrollbarCompensation;

		const maxHeight = isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : chartHeight.max;

		// For AskKuralInline, cap strictly — don't let chartHeight.min override the max
		if (isRenderedFromAskKuralInline) {
			return Math.min(maxHeight, Math.max(0, availableHeight));
		}
		if (isEffectiveMultiTile) {
			return Math.min(maxHeight, Math.max(0, availableHeight));
		}

		return Math.max(chartHeight.min, Math.min(maxHeight, availableHeight));
	}, [contentAreaHeight, tableHeaderHeight, lineChartConfig, needsScroll, isRenderedFromAskKuralInline, isEffectiveMultiTile]);

	const gridTemplate = `repeat(${totalColumns}, ${columnWidth}px)`;

	const allAttributeColumns = useMemo(() => {
		const cols: {
			groupIdx: number;
			attrIdx: number;
			attribute: string;
			isFirstInGroup: boolean;
			isLastInGroup: boolean;
			globalColIndex: number;
		}[] = [];
		let globalIdx = 0;
		hierarchyGroups.forEach((group, groupIdx) => {
			// Filter attributes based on visibleWaves
			const filteredAttributes = group.attributes.filter((attr) => visibleWaves.includes(attr));

			filteredAttributes.forEach((attr, attrIdx) => {
				cols.push({
					groupIdx,
					attrIdx,
					attribute: attr,
					isFirstInGroup: attrIdx === 0,
					isLastInGroup: attrIdx === filteredAttributes.length - 1,
					globalColIndex: globalIdx,
				});
				globalIdx++;
			});
		});
		return cols;
	}, [hierarchyGroups, visibleWaves]); // Add visibleWaves dependency

	// Derived state to detect if C3 (Z1 axis) is applied
	const hasC3Applied = useMemo(() => {
		const z1Axis = chartResponse.axis_idf?.z1;
		const has = z1Axis !== null && z1Axis !== undefined;
		return has;
	}, [chartResponse.axis_idf?.z1]);

	if (isDataEmpty) {
		return <ChartError errorMessage="No data available" setChartData={() => { }} setIsChartLoading={() => { }} is_retry={false} />;
	}

	return (
		<ChartContainer innerRef={containerRef} renderedFrom={renderedFrom}>
			{/* HEADER: Title + Legends */}
			<div ref={headerSectionRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={productColors}
					visibleWaves={visibleProducts}
					setVisibleWaves={setVisibleProducts}
					expanded={expandedProducts}
					setExpanded={setExpandedProducts}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={isChartView}
					setIsChartView={setIsChartView}
					showToggle={scaled.layout.showViewToggle}
					scaledTitleFontSize={pxToRem(scaled.typography.title)}
					scaledLegendFontSize={scaled.typography.legend}
					showSubtitle={true}
					showLegend={hasProducts && scaled.layout.showLegend}
					waveMarkers={waveMarkers}
					renderedFrom={renderedFrom}
				/>
			</div>

			{/* CONTENT AREA */}
			{isChartView ? (
				<div ref={contentAreaRef} className="flex flex-1 min-h-0 overflow-hidden relative">
					{/* Y-Axis Label */}
					{isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile && (
						<AxisLabel
							label={chartResponse?.y_axis_label || chartResponse?.chart_config?.y_lbl || "Attributes"}
							orientation="vertical"
							maxHeight={contentAreaHeight || undefined}
						/>
					)}

					<div
						className={`h-full w-full ${needsScroll ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-hidden`}
						style={{
							borderRight: `1px solid ${colorsConfig.borders.solid}`,
							...(isEffectiveMultiTile && !isRenderedFromAskKural && { borderLeft: `1px solid ${colorsConfig.borders.solid}` }),
						}}>
						<div className={needsScroll ? "inline-block min-w-full" : "w-full"}>
							{/* HIERARCHY HEADERS */}
							<div ref={tableHeaderRef}>
						{hasHierarchy &&
							effectiveHierarchyLevels.map((_: any, levelIndex: number) => {
								const headers = buildHeaderRow(hierarchyGroups, levelIndex, visibleWaves);
								const isLastLevel = levelIndex === effectiveHierarchyLevels.length - 1;

								if (headers.length === 0) return null;

									let colOffset = 0;
									return (
										<div
											key={levelIndex}
											className="grid"
											style={{
												gridTemplateColumns: `${showAxisColumn ? `${scaled.geometry.axisColumnWidth}px ` : ""}${gridTemplate}`,
												minHeight: pxToRem(scaled.layout.headerRowHeight),
											}}>
												{showAxisColumn && (
													<div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />
												)}
												{headers.map((cell, idx) => {
													const textClass = getHeaderTextStyle(levelIndex, isLastLevel, scaled.scaleFactor);
													colOffset += cell.colSpan;
													const endColIndex = colOffset - 1;
													const borders = getBorders("header", levelIndex, endColIndex);

													return (
														<div
															key={idx}
															className={`text-center flex flex-col items-center justify-center ${textClass.className}`}
															style={{
																gridColumn: `span ${cell.colSpan}`,
																minHeight: pxToRem(scaled.layout.headerRowHeight),
																padding: `${scaled.geometry.cellPaddingV}px ${scaled.geometry.cellPaddingH}px`,
																...borders,
															}}>
															<EllipsisText
																text={cell.label}
																maxLines={isEffectiveMultiTile ? 3 : 4}
																lineHeight={textClass.lineHeight}
																fontSize={pxToRem(textClass.fontSize)}
																className="text-center w-full"
															/>
														</div>
													);
												})}
											</div>
										);
									})}

								{/* ATTRIBUTES ROW */}
								<div className="grid" style={{ gridTemplateColumns: `${showAxisColumn ? `${scaled.geometry.axisColumnWidth}px ` : ""}${gridTemplate}` }}>
									{showAxisColumn && <div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />}
									{allAttributeColumns.map((col, idx) => {
										const bgColor = col.attrIdx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
										const borders = getBorders("attribute", 0, col.globalColIndex);

										return (
											<div
												key={idx}
												className="flex items-center justify-center"
												style={{
													backgroundColor: bgColor,
													color: colorsConfig.text.tertiary,
													padding: `${scaled.geometry.cellPaddingV}px ${scaled.geometry.cellPaddingH}px`,
													minHeight: pxToRem(scaled.layout.headerRowHeight),
													...borders,
												}}>
										<EllipsisText
											text={col.attribute}
											maxLines={isEffectiveMultiTile ? 3 : 4}
													lineHeight={scaled.typography.axisLabel + 4}
													fontSize={pxToRem(scaled.typography.axisLabel)}
													className="font-normal text-center w-full"
												/>
											</div>
										);
									})}
								</div>

								{/* N-SIZE ROW */}
								<div className="grid" style={{ gridTemplateColumns: `${showAxisColumn ? `${scaled.geometry.axisColumnWidth}px ` : ""}${gridTemplate}` }}>
									{showAxisColumn && <div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />}
									{allAttributeColumns.map((col, idx) => {
										const bgColor = col.attrIdx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
										const nSize = hierarchyGroups[col.groupIdx].attributeNSize[col.attribute];
										const borders = getBorders("nsize", 0, col.globalColIndex);

										return (
											<div
												key={`nsize-${idx}`}
												className="flex items-center justify-center"
												style={{
													backgroundColor: bgColor,
													padding: `${Math.max(1, Math.floor(scaled.geometry.cellPaddingV / 2))}px 0`,
													minHeight: pxToRem(scaled.layout.nSizeRowHeight),
													...borders,
												}}>
												<NSizeLabel nSize={nSize} isCompact={isEffectiveMultiTile} fontSize={scaled.typography.nSize} />
											</div>
										);
									})}
								</div>
							</div>

							{/* CHART AREA — uses same grid pattern as bar charts */}
							<div className="grid relative" style={{ gridTemplateColumns: `${showAxisColumn ? `${scaled.geometry.axisColumnWidth}px ` : ""}${gridTemplate}` }}>
								{showAxisColumn && (
									<div
										className="sticky left-0 z-20 bg-white flex flex-col justify-end"
										style={{ height: dynamicChartHeight, borderRight: `1px solid ${colorsConfig.borders.solid}` }}>
										<YAxisScale
											height={dynamicChartHeight}
											paddingTop={lineChartConfig.axisTop}
											paddingBottom={lineChartConfig.axisBottom}
											domain={yAxisDomain}
											labelFormat={labelFormat}
											formatDataLabel={formatDataLabel}
											fontSize={pxToRem(scaled.typography.axisLabel)}
										/>
									</div>
								)}

								{/* Background cells for alternating colors */}
								{allAttributeColumns.map((col, idx) => {
									const bgColor = col.attrIdx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
									const borders = getBorders("chart", 0, col.globalColIndex);

									return (
										<div
											key={idx}
											className="flex justify-center items-end"
											style={{
												height: dynamicChartHeight,
												backgroundColor: bgColor,
												...borders,
											}}
										/>
									);
								})}

								{/* Chart overlay — positioned absolutely over the data columns */}
								<div
									className="absolute flex"
									style={{
										top: 0,
										left: showAxisColumn ? scaled.geometry.axisColumnWidth : 0,
										height: dynamicChartHeight,
										pointerEvents: "none",
									}}>
									{hierarchyGroups.map((group, groupIdx) => {
										const filteredAttrs = group.attributes.filter((attr) => visibleWaves.includes(attr));
										if (filteredAttrs.length === 0) return null;

										const groupWidth = filteredAttrs.length * columnWidth;

										const isGroupAllEmpty = !filteredAttrs.some((attr) =>
											visibleProducts.some((product) => (group.productData[product]?.[attr]?.length ?? 0) > 0)
										);

										if (isGroupAllEmpty) {
											return (
												<div
													key={groupIdx}
													className="flex items-center justify-center"
													style={{ width: groupWidth, height: dynamicChartHeight, pointerEvents: "auto" }}>
													<span className="text-xs text-gray-400">No data</span>
												</div>
											);
										}

										return (
											<div key={groupIdx} style={{ width: groupWidth, height: dynamicChartHeight, pointerEvents: "auto" }}>
												<MiniHLineChart
													attributes={filteredAttrs}
													productData={group.productData}
													productColors={productColors}
													waveMarkers={waveMarkers}
													visibleProducts={visibleProducts}
													visibleWaves={visibleWaves}
													yAxisDomain={yAxisDomain}
													labelFormat={labelFormat}
													height={dynamicChartHeight}
													columnWidth={columnWidth}
													lineWeight={lineWeight}
													smoothLine={smoothLine}
													statTestConfig={statTestConfig}
													renderedFrom={renderedFrom}
													labelOverlapThreshold={isEffectiveMultiTile ? 10 : 10}
												/>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div
					ref={tableViewContainerRef}
					style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", justifyContent: "center", alignItems: "center" }}>
					<ChartTabularView
						chartData={apiData}
						//chartType="v_cluster_column" //HlineChart table view same as HClusterBar
						chartType="h_cluster_bar"
						tableContainerHeight={tableViewContainerHeight}
						parentContainerWidth={containerWidth}
						labelFormat={chartResponse?.chart_config?.dlabel_fmt}
						statTestConfig={statTestConfig}
						renderedFrom={renderedFrom}
						filterHierarchy={explicitHierarchy}
						seriesOrder={seriesOrder}
					/>
				</div>
			)}
			{/* X-Axis Label */}
			{isChartView && chartResponse?.x_axis_label && !isEffectiveMultiTile && (
				<AxisLabel label={chartResponse?.chart_config?.x_lbl || chartResponse?.x_axis_label} orientation="horizontal" className="mt-2" />
			)}

			<ChartFootnote
				hasLowNSize={showLowNSize}
				src={chartResponse.footnote?.src}
				footnoteObj={chartResponse.footnote}
				isWeighted={chartResponse.is_weight}
				className={`py-3 mt-auto`}
				analysis_name={chartResponse.analysis_type}
				stat_message={chartResponse.footnote?.stat_message}
				valueParams={chartResponse?.value_params}
				renderedFrom={renderedFrom}
			/>
		</ChartContainer>
	);
};

export default HLineChart;
